import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Users, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAcceptInvite, useRejectInvite } from "@/hooks/useMates";
import { useToastHandler } from "@/hooks/useToastHandler";
import PageTitleScript from "@/components/MetaScript";

export function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [autoAccept, setAutoAccept] = useState(true);
  const { handleSuccess } = useToastHandler();

  const acceptMutation = useAcceptInvite();
  const rejectMutation = useRejectInvite();

  // Auto-accept invite on page load if autoAccept is true
  useEffect(() => {
    if (token && autoAccept) {
      acceptMutation.mutate(token, {
        onSuccess: () => {
          handleSuccess("You're mates now!", 2000);
          // Redirect to mates page after success
          setTimeout(() => {
            navigate("/settings/mates");
          }, 2000);
        },
        onError: () => {
          // On error, show manual accept/reject options
          setAutoAccept(false);
        },
      });
    }
  }, [token, autoAccept, acceptMutation, navigate]);

  const handleAccept = () => {
    if (!token) return;

    acceptMutation.mutate(token, {
      onSuccess: () => {
        handleSuccess("You're mates now!", 2000);
        setTimeout(() => {
          navigate("/settings/mates");
        }, 2000);
      },
    });
  };

  const handleReject = () => {
    if (!token) return;

    rejectMutation.mutate(token, {
      onSuccess: () => {
        handleSuccess("Invitation declined.", 2000);
        setTimeout(() => {
          navigate("/settings/mates");
        }, 2000);
      },
    });
  };

  const goToMates = () => {
    navigate("/settings/mates");
  };

  // Loading state (auto-accepting)
  if (autoAccept && acceptMutation.isPending) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
        <PageTitleScript title="Accepting Invitation" />

        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto" />
          <h1 className="text-2xl font-bold text-tx-dark">
            Accepting invitation...
          </h1>
          <p className="text-tx-muted">
            Please wait while we process your invitation.
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (acceptMutation.isSuccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
        <PageTitleScript title="Invitation Accepted" />

        <div className="text-center space-y-6 max-w-md">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />

          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-tx-dark">
              You're mates now!
            </h1>
            <p className="text-tx-muted">
              You can now see each other's workouts and keep each other
              motivated.
            </p>
          </div>

          <Button onClick={goToMates} className="w-full">
            <Users className="h-4 w-4 mr-2" />
            View Workout Mates
          </Button>
        </div>
      </div>
    );
  }

  // Rejection success state
  if (rejectMutation.isSuccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
        <PageTitleScript title="Invitation Declined" />

        <div className="text-center space-y-6 max-w-md">
          <XCircle className="h-16 w-16 text-gray-500 mx-auto" />

          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-tx-dark">
              Invitation declined
            </h1>
            <p className="text-tx-muted">
              You have successfully declined the workout mate invitation.
            </p>
          </div>

          <Button onClick={goToMates} variant="outline" className="w-full">
            <Users className="h-4 w-4 mr-2" />
            Back to Workout Mates
          </Button>
        </div>
      </div>
    );
  }

  // Error state or manual accept/reject
  if (acceptMutation.isError || rejectMutation.isError || !autoAccept) {
    const error = acceptMutation.error || rejectMutation.error;
    const errorMessage = error?.response?.data?.message || "An error occurred";

    // Check for specific error types
    const isExpired = errorMessage.toLowerCase().includes("expired");
    const isAlreadyMates = errorMessage.toLowerCase().includes("already mates");
    const isLimitReached = errorMessage.toLowerCase().includes("limit");

    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
        <PageTitleScript title="Workout Mate Invitation" />

        <div className="text-center space-y-6 max-w-md">
          {isExpired || isAlreadyMates || isLimitReached ? (
            <>
              <XCircle className="h-16 w-16 text-tx-error mx-auto" />
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-tx-dark">
                  {isExpired && "Invitation Expired"}
                  {isAlreadyMates && "Already Mates"}
                  {isLimitReached && "Mate Limit Reached"}
                </h1>
                <p className="text-tx-muted">{errorMessage}</p>
              </div>
              <Button onClick={goToMates} variant="outline" className="w-full">
                <Users className="h-4 w-4 mr-2" />
                Back to Workout Mates
              </Button>
            </>
          ) : (
            <>
              <Users className="h-16 w-16 text-blue-500 mx-auto" />
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-tx-dark">
                  Workout Mate Invitation
                </h1>
                <p className="text-tx-muted">
                  Would you like to become workout mates? You'll be able to see
                  each other's workouts and stay motivated together.
                </p>
                {error && (
                  <p className="text-sm text-tx-error mt-2">{errorMessage}</p>
                )}
              </div>

              <div className="flex flex-col gap-3 w-full">
                <Button
                  onClick={handleAccept}
                  disabled={acceptMutation.isPending}
                  className="w-full"
                >
                  {acceptMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Accepting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Accept Invitation
                    </>
                  )}
                </Button>

                <Button
                  onClick={handleReject}
                  disabled={rejectMutation.isPending}
                  variant="outline"
                  className="w-full"
                >
                  {rejectMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Declining...
                    </>
                  ) : (
                    "Decline"
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Fallback state
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
      <PageTitleScript title="Invalid Invitation" />

      <div className="text-center space-y-6 max-w-md">
        <XCircle className="h-16 w-16 text-tx-error mx-auto" />

        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-tx-dark">
            Invalid Invitation
          </h1>
          <p className="text-tx-muted">
            This invitation link is invalid or missing required information.
          </p>
        </div>

        <Button onClick={goToMates} variant="outline" className="w-full">
          <Users className="h-4 w-4 mr-2" />
          Back to Workout Mates
        </Button>
      </div>
    </div>
  );
}
